import { homePath } from "@/lib/roles";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import ConnectionBanner from "@/components/ConnectionBanner";
import ProtectedRoute from "@/components/ProtectedRoute";
import Navbar from "@/components/Navbar";
import AuthPage from "@/pages/AuthPage";
import AuthCallback from "@/pages/AuthCallback";
import StudentDashboard from "@/pages/StudentDashboard";
import MathPractice from "@/pages/MathPractice";
import ArcadeGame from "@/pages/ArcadeGame";
import ArcadeLobby from "@/pages/ArcadeLobby";
import TeacherDashboard from "@/pages/TeacherDashboard";
import ShopPage from "@/pages/ShopPage";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";

function AppRoutes() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-void">
        <div className="font-mono text-cyber text-lg tracking-widest animate-pulse">
          INITIALIZING...
        </div>
      </div>
    );
  }

  return (
    <>
      {user && <Navbar />}
      <ConnectionBanner />
      <Routes>
        <Route
          path="/"
          element={
            user ? (
              <Navigate to={homePath(user.role)} replace />
            ) : (
              <AuthPage />
            )
          }
        />
        <Route path="/admin" element={user ? <Navigate to={homePath(user.role)} replace /> : <AdminLogin />} />
        <Route
          path="/admin/dashboard"
          element={
            <ProtectedRoute role="admin">
              <AdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route path="/login" element={user ? <Navigate to="/" replace /> : <AuthPage />} />
        <Route path="/register" element={user ? <Navigate to="/" replace /> : <AuthPage initialTab="register" />} />
        <Route
          path="/student"
          element={
            <ProtectedRoute role="student">
              <StudentDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/practice"
          element={
            <ProtectedRoute role="student">
              <MathPractice />
            </ProtectedRoute>
          }
        />
        <Route
          path="/arcade"
          element={
            <ProtectedRoute role="student">
              <ArcadeLobby />
            </ProtectedRoute>
          }
        />
        <Route
          path="/arcade/play/:gameId"
          element={
            <ProtectedRoute role="student">
              <ArcadeGame />
            </ProtectedRoute>
          }
        />
        <Route
          path="/shop"
          element={
            <ProtectedRoute role="student">
              <ShopPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/teacher"
          element={
            <ProtectedRoute role="teacher">
              <TeacherDashboard />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
// Check for Google OAuth callback BEFORE normal routes render
function AppRouter() {
  const location = useLocation();

  // Detect session_id from Emergent Google Auth — synchronous check (not in useEffect)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return <AppRoutes />;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <div className="starfield" aria-hidden="true" />
        <div className="crt-overlay" aria-hidden="true" />
        <AppRouter />
        <Toaster
          position="top-right"
          toastOptions={{
            style: {
              background: "#0A0A10",
              border: "1px solid rgba(0,240,255,0.2)",
              color: "#fff",
            },
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}
